import type { PayoutPolicy, RuleSet } from './types.js';
import type { PlanInput } from './schema.js';

/*
 * SAMPLE CATALOGUE
 * ----------------
 * Designed sample terms for CK Funded, CK Futures and CK Preps. They seed the
 * catalogue on first run; after that, staff edit plans in the CRM
 * (crm.ckfundedpro.com → Plans & rules) and these values are no longer used.
 * Every plan is marked final: false until CK approves the terms.
 */

const r = (o: Partial<RuleSet> & Pick<RuleSet, 'maxLossPct'>): RuleSet => ({
  profitTargetPct: null,
  maxDailyLossPct: null,
  dailyLossBasis: 'max_of_balance_equity',
  maxLossType: 'static',
  minTradingDays: 0,
  maxCalendarDays: null,
  ...o,
});

const biweekly: PayoutPolicy = { firstPayoutAfterDays: 14, cycleDays: 14, minProfitableDays: 3, minPayoutUsd: 50 };
const weekly: PayoutPolicy = { firstPayoutAfterDays: 7, cycleDays: 7, minProfitableDays: 3, minPayoutUsd: 50 };

const usd = (prices: Record<number, number>) =>
  Object.fromEntries(Object.entries(prices).map(([k, v]) => [k, Math.round(v * 100)]));

export const SAMPLE_CATALOG: PlanInput[] = [
  // ---------------- CK Funded (FX & CFD evaluations) ----------------
  {
    id: 'prop-classic', brand: 'prop', name: 'Classic', tag: 'Two-step',
    description: 'The proven route, built for patience. Two targets, generous room.',
    rewardSplitPct: 80, leverage: 100, payout: biweekly, active: true, final: false,
    pricesUsdCents: usd({ 5000: 39, 10000: 69, 25000: 159, 50000: 279, 100000: 499, 200000: 949 }),
    phases: [
      { key: 'p1', name: 'Phase 1', rules: r({ profitTargetPct: 8, maxDailyLossPct: 5, maxLossPct: 10, minTradingDays: 4 }) },
      { key: 'p2', name: 'Phase 2', rules: r({ profitTargetPct: 5, maxDailyLossPct: 5, maxLossPct: 10, minTradingDays: 4 }) },
      { key: 'funded', name: 'CK Funded', funded: true, rules: r({ maxDailyLossPct: 5, maxLossPct: 10 }) },
    ],
  },
  {
    id: 'prop-rapid', brand: 'prop', name: 'Rapid', tag: 'One-step',
    description: 'One target, one phase, straight to rewards.',
    rewardSplitPct: 90, leverage: 30, payout: biweekly, active: true, final: false,
    pricesUsdCents: usd({ 5000: 49, 10000: 89, 25000: 199, 50000: 329, 100000: 579, 200000: 1099 }),
    phases: [
      { key: 'p1', name: 'Evaluation', rules: r({ profitTargetPct: 10, maxDailyLossPct: 4, maxLossPct: 6, maxLossType: 'trailing', minTradingDays: 3 }) },
      { key: 'funded', name: 'CK Funded', funded: true, rules: r({ maxDailyLossPct: 4, maxLossPct: 6, maxLossType: 'trailing' }) },
    ],
  },
  {
    id: 'prop-instant', brand: 'prop', name: 'Instant', tag: 'No evaluation',
    description: 'Skip the test. Trade a simulated funded account today.',
    rewardSplitPct: 70, leverage: 30, payout: { ...biweekly, minProfitableDays: 5 }, active: true, final: false,
    pricesUsdCents: usd({ 5000: 69, 10000: 129, 25000: 299, 50000: 549, 100000: 999 }),
    phases: [
      { key: 'funded', name: 'CK Funded', funded: true, rules: r({ maxDailyLossPct: 3, maxLossPct: 6, maxLossType: 'trailing' }) },
    ],
  },
  // ---------------- CK Futures ----------------
  {
    id: 'futures-builder', brand: 'futures', name: 'Builder', tag: 'Easiest first payout',
    description: 'The gentlest way into futures. No daily limit, end-of-day trailing drawdown.',
    rewardSplitPct: 80, leverage: 1, payout: { firstPayoutAfterDays: 5, cycleDays: 5, minProfitableDays: 5, minPayoutUsd: 100 }, active: true, final: false,
    pricesUsdCents: usd({ 25000: 59, 50000: 99, 100000: 149, 150000: 199 }),
    phases: [
      { key: 'eval', name: 'Evaluation', rules: r({ profitTargetPct: 6, maxLossPct: 4, maxLossType: 'trailing', minTradingDays: 1 }) },
      { key: 'funded', name: 'Sim funded', funded: true, rules: r({ maxLossPct: 4, maxLossType: 'trailing' }) },
    ],
  },
  {
    id: 'futures-rapid', brand: 'futures', name: 'Rapid', tag: 'Daily payouts',
    description: 'Pass in days, request rewards daily.',
    rewardSplitPct: 90, leverage: 1, payout: { firstPayoutAfterDays: 1, cycleDays: 1, minProfitableDays: 1, minPayoutUsd: 100 }, active: true, final: false,
    pricesUsdCents: usd({ 25000: 99, 50000: 149, 100000: 249, 150000: 349 }),
    phases: [
      { key: 'eval', name: 'Evaluation', rules: r({ profitTargetPct: 6, maxDailyLossPct: 2, dailyLossBasis: 'balance', maxLossPct: 4, maxLossType: 'trailing', minTradingDays: 2 }) },
      { key: 'funded', name: 'Sim funded', funded: true, rules: r({ maxDailyLossPct: 2, dailyLossBasis: 'balance', maxLossPct: 4, maxLossType: 'trailing' }) },
    ],
  },
  {
    id: 'futures-pro', brand: 'futures', name: 'Pro', tag: 'Highest ceiling',
    description: 'Larger contract limits for scaled traders.',
    rewardSplitPct: 90, leverage: 1, payout: { firstPayoutAfterDays: 14, cycleDays: 14, minProfitableDays: 5, minPayoutUsd: 250 }, active: true, final: false,
    pricesUsdCents: usd({ 50000: 199, 100000: 329, 150000: 449 }),
    phases: [
      { key: 'eval', name: 'Evaluation', rules: r({ profitTargetPct: 6, maxDailyLossPct: 3, dailyLossBasis: 'balance', maxLossPct: 5, maxLossType: 'trailing', minTradingDays: 2 }) },
      { key: 'funded', name: 'Sim funded', funded: true, rules: r({ maxDailyLossPct: 3, dailyLossBasis: 'balance', maxLossPct: 5, maxLossType: 'trailing' }) },
    ],
  },
  // ---------------- CK Preps (24/7 perpetuals) ----------------
  {
    id: 'perps-spark', brand: 'perps', name: 'Spark', tag: 'Entry',
    description: 'Get a feel for perps with tight risk.',
    rewardSplitPct: 80, leverage: 5, payout: weekly, active: true, final: false,
    pricesUsdCents: usd({ 5000: 49, 10000: 89, 25000: 179, 50000: 299 }),
    phases: [
      { key: 'p1', name: 'Phase 1', rules: r({ profitTargetPct: 8, maxDailyLossPct: 4, maxLossPct: 8, minTradingDays: 3 }) },
      { key: 'p2', name: 'Phase 2', rules: r({ profitTargetPct: 5, maxDailyLossPct: 4, maxLossPct: 8, minTradingDays: 3 }) },
      { key: 'funded', name: 'Sim funded', funded: true, rules: r({ maxDailyLossPct: 4, maxLossPct: 8 }) },
    ],
  },
  {
    id: 'perps-pulse', brand: 'perps', name: 'Pulse', tag: 'One-step',
    description: 'Single phase, built for momentum traders.',
    rewardSplitPct: 85, leverage: 10, payout: weekly, active: true, final: false,
    pricesUsdCents: usd({ 5000: 59, 10000: 109, 25000: 219, 50000: 369 }),
    phases: [
      { key: 'p1', name: 'Evaluation', rules: r({ profitTargetPct: 10, maxDailyLossPct: 3, maxLossPct: 6, maxLossType: 'trailing', minTradingDays: 3 }) },
      { key: 'funded', name: 'Sim funded', funded: true, rules: r({ maxDailyLossPct: 3, maxLossPct: 6, maxLossType: 'trailing' }) },
    ],
  },
  {
    id: 'perps-apex', brand: 'perps', name: 'Apex', tag: 'Pro',
    description: 'Bigger size, wider room, faster rewards.',
    rewardSplitPct: 90, leverage: 20, payout: weekly, active: true, final: false,
    pricesUsdCents: usd({ 25000: 249, 50000: 429, 100000: 799 }),
    phases: [
      { key: 'p1', name: 'Evaluation', rules: r({ profitTargetPct: 12, maxDailyLossPct: 4, maxLossPct: 8, minTradingDays: 4, maxCalendarDays: 60 }) },
      { key: 'funded', name: 'Sim funded', funded: true, rules: r({ maxDailyLossPct: 4, maxLossPct: 8 }) },
    ],
  },
];
